import { useState } from "react";
import type { BootstrapConfig, LlmCheckResponse } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/api";

interface SettingsPageProps {
  config: BootstrapConfig;
}

export function SettingsPage({ config }: SettingsPageProps): import("react").ReactElement {
  const [llmCheck, setLlmCheck] = useState<LlmCheckResponse | { ok: false; error: string } | null>(null);
  const [checking, setChecking] = useState(false);

  async function runLlmCheck(): Promise<void> {
    setChecking(true);
    try {
      setLlmCheck(await adminApi.llmCheck());
    } catch (error) {
      setLlmCheck({
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Runtime metadata from tenant YAML.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Tenant</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><span className="font-medium">Name:</span> {config.tenant.name}</div>
            <div><span className="font-medium">Industry:</span> {config.tenant.industry}</div>
            <div><span className="font-medium">CRM:</span> {config.features.crm ? "enabled" : "disabled"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Tool Surface</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {[...config.tools.common, ...config.tools.backend].map((tool) => (
                <span key={tool} className="rounded-md border bg-muted px-2 py-1 text-xs font-medium">{tool}</span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle>LLM Connectivity</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={runLlmCheck} disabled={checking}>
            {checking ? "Checking..." : "Check OpenAI API"}
          </Button>
          {llmCheck && (
            <pre className="max-h-[220px] overflow-auto rounded-md border bg-muted p-3 text-xs">
              {JSON.stringify(llmCheck, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Bootstrap Payload</CardTitle></CardHeader>
        <CardContent>
          <pre className="max-h-[520px] overflow-auto rounded-md border bg-muted p-3 text-xs">
            {JSON.stringify(config, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
